import { ColumnDefinition, EntityInterface } from "./Table";

/**
 * Interface DTO for a person/user in the people table
 */
export interface PersonDto {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    group: string;
    status: 'active' | 'pending' | 'deactivated' | 'terminated' | 'suspended';
    lastActiveDate: Date | null;
}

/**
 * Person class representing a user in the people table
 */
export class Person implements EntityInterface {
    /**
     * Constructor for the Person class
     * 
     * @param id 
     * @param firstName 
     * @param lastName 
     * @param email 
     * @param group 
     * @param lastActiveDate 
     * @param status
     */
    constructor(
        public id: string,
        public firstName: string,
        public lastName: string,
        public email: string,
        public group: string,
        public lastActiveDate: Date | null,
        public status: 'active' | 'pending' | 'deactivated' | 'terminated' | 'suspended',
    ) { }

    /**
     * Creates a copy of the current Person instance
     * 
     * @returns A copy of the current Person instance
     */
    copyOf(): Person {
        return new Person(
            this.id,
            this.firstName,
            this.lastName,
            this.email,
            this.group,
            this.lastActiveDate,
            this.status,
        );
    }

    /**
     * Return true if the current Person instance is equal to the given person
     * 
     * @param other 
     * @returns 
     */
    equals(other: Person): boolean {
        if (!other) return false;

        return this.id === other.id &&
            this.firstName === other.firstName &&
            this.lastName === other.lastName &&
            this.email === other.email &&
            this.group === other.group &&
            this.lastActiveDate === other.lastActiveDate &&
            this.status === other.status;
    }

    /**
     * Returns a string representation of the Person instance for filtering
     * 
     * @param defs The column definitions to use for formatting
     * @returns A string representation of the Person instance
     */
    toString(defs: ColumnDefinition<any>[]): string {
        let str = "";
        for (let col of defs) {
            if (col.toStringTransform != undefined) {
                str += col.toStringTransform((this as any)[col.field]) + "█";
            } else {
                str += (this as any)[col.field];
            }
        }

        return str.toLowerCase();
    }

    /** Specialized getters */

    get fullName(): string {
        return `${this.firstName} ${this.lastName}`;
    }

    get lastActive(): string {
        // Return empty if lastActiveDate is undefined or null
        if (this.lastActiveDate == undefined) return "";

        // Calculate the difference in time from now
        const now = new Date();
        const lastActive = new Date(this.lastActiveDate);
        const diffInMs = now.getTime() - lastActive.getTime();
        const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
        const diffInDays = Math.floor(diffInHours / 24);

        // Format the last active time based on the difference
        if (diffInHours <= 0) {
            return 'Just now';
        } else if (diffInHours < 24) {
            return diffInHours === 1 ? '1 hour ago' : `${diffInHours} hours ago`;
        } else if (diffInDays < 30) {
            return diffInDays === 1 ? '1 day ago' : `${diffInDays} days ago`;
        } else {
            return 'Over a month';
        }
    }
}

/** Mapper for converting between PersonDto and Person */
export class PersonMapper {
    static toDto(person: Person): PersonDto {
        // Apply timezone offset to standardize to UTC
        if (person.lastActiveDate) {
            const utcDate = new Date(person.lastActiveDate);
            utcDate.setMinutes(utcDate.getMinutes() + utcDate.getTimezoneOffset());
            person.lastActiveDate = utcDate;
        }

        return {
            id: person.id,
            firstName: person.firstName,
            lastName: person.lastName,
            email: person.email,
            group: person.group,
            lastActiveDate: person.lastActiveDate,
            status: person.status,
        };
    }

    static toModel(dto: PersonDto): Person {
        // Apply timezone offset to local
        if (dto.lastActiveDate) {
            const localDate = new Date(dto.lastActiveDate);
            localDate.setMinutes(localDate.getMinutes() - localDate.getTimezoneOffset());
            dto.lastActiveDate = localDate;
        }

        return new Person(
            dto.id,
            dto.firstName,
            dto.lastName,
            dto.email,
            dto.group,
            dto.lastActiveDate,
            dto.status,
        );
    }
}